/*
 * PRD-037 — Clarity-only instrumentation for the standalone Partner app.
 *
 * Delegates to the shared low-level wrapper (`@utils/analytics/clarity`, no PostHog). Every call is
 * a no-op when Clarity did not load (REACT_APP_PUBLIC_ENABLE_TRACKING off / non-prod) — the wrapper
 * already guards each SDK call with try/catch, so nothing here can throw at runtime.
 *
 * Clarity API constraints baked into this API:
 *  - `event(name)` takes NO props, so every dimension travels as a tag (`partnerTag`).
 *  - Tags are last-write-wins per session, so sequential steps reuse a SINGLE key per dimension
 *    (`wizard_step`, `claim_step`) that gets overwritten — never N booleans.
 *  - NEVER pass PII (phone/email) as a tag value. Only opaque ids (partner_id, id_commerce, plan key)
 *    or bounded enums are allowed.
 */
import { clarityEvent, clarityIdentify, claritySetTags, clarityUpgrade } from '@utils/analytics/clarity';

type PartnerTags = {
  origin?: 'partner_portal' | 'pre_registro';
  partner_id?: string;
  id_commerce?: string;
  wizard_step?: string; // general | tipo | direccion | entrega | horarios | menu | invitar
  claim_step?: string; // open | otp_verified | submit | activated
  error_reason?: string; // otp_invalid | token_expired | link_used | missing_fields | no_module | bad_credentials | not_found | otp_send_failed | username_taken | unknown
  claim_result?: 'success' | 'error';
  plan_id?: string; // plan key only (e.g. PROFESIONAL / PROFESIONAL_A) — never PII
};

// Applies session tags (dimensions). Truthy-only + last-write-wins are handled by the wrapper.
export const partnerTag = (tags: PartnerTags): void => {
  claritySetTags(tags);
};

// Records a smart event. Name only — dimensions must go through `partnerTag`.
export const partnerEvent = (name: string): void => {
  clarityEvent(name);
};

// Ties the current session to a stable opaque id (partner_id). `friendly` is a display label only.
export const partnerIdentify = (id: string, friendly?: string): void => {
  clarityIdentify(id, friendly);
};

// Pins the current session's replay at high-value / failure moments (Clarity samples to 100k/day,
// so critical low-volume funnels must be forced to keep the recording).
export const partnerUpgrade = (reason: string): void => {
  clarityUpgrade(reason);
};
